'use client';

import { useState, useEffect } from 'react';
import KnowledgeSourceSelector from './KnowledgeSourceSelector';
import ContentTypeFilter from './ContentTypeFilter';
import MetricsStrip from './MetricsStrip';
import KnowledgeMapVisualization from './KnowledgeMapVisualization';
import ContentRecommendations from './ContentRecommendations';
import ContentPerformanceTable from './ContentPerformanceTable';

interface KnowledgeSource {
  id: string;
  name: string;
  icon: string;
  color: string;
  enabled: boolean;
}

interface ContentType {
  id: string;
  label: string;
  icon: string;
  count: number;
}

interface Metric {
  id: string;
  label: string;
  value: string;
  change: number;
  changeLabel: string;
  icon: string;
  color: string;
}

interface KnowledgeNode {
  id: string;
  title: string;
  source: string;
  citations: number;
  satisfaction: number;
  freshness: 'fresh' | 'aging' | 'stale';
  category: string;
}

interface Recommendation {
  id: string;
  type: 'update' | 'create' | 'replicate';
  title: string;
  reason: string;
  priority: 'high' | 'medium' | 'low';
  impact: string;
  source?: string;
}

interface ContentArticle {
  id: string;
  title: string;
  source: string;
  citations: number;
  satisfaction: number;
  lastUpdated: string;
  status: 'approved' | 'pending' | 'draft';
  category: string;
  views: number;
}

export default function KnowledgePerformanceInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedSources, setSelectedSources] = useState<string[]>(['notion', 'confluence', 'gdrive']);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(['guide', 'faq', 'tutorial', 'troubleshooting']);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const knowledgeSources: KnowledgeSource[] = [
    { id: 'notion', name: 'Notion', icon: 'DocumentTextIcon', color: 'bg-gradient-to-br from-accent-cyan to-accent-gold', enabled: true },
    { id: 'confluence', name: 'Confluence', icon: 'BookOpenIcon', color: 'bg-gradient-to-br from-accent-gold to-accent-cyan', enabled: true },
    { id: 'gdrive', name: 'Google Drive', icon: 'FolderIcon', color: 'bg-gradient-to-br from-accent-cyan to-accent-gold', enabled: true },
    { id: 'jira', name: 'Jira', icon: 'TicketIcon', color: 'bg-gradient-to-br from-accent-gold to-accent-cyan', enabled: true },
    { id: 'slack', name: 'Slack', icon: 'ChatBubbleLeftRightIcon', color: 'bg-gradient-to-br from-accent-cyan to-accent-gold', enabled: true }
  ];

  const contentTypes: ContentType[] = [
    { id: 'guide', label: 'User Guides', icon: 'BookOpenIcon', count: 142 },
    { id: 'faq', label: 'FAQs', icon: 'QuestionMarkCircleIcon', count: 89 },
    { id: 'tutorial', label: 'Tutorials', icon: 'AcademicCapIcon', count: 67 },
    { id: 'troubleshooting', label: 'Troubleshooting', icon: 'WrenchScrewdriverIcon', count: 54 }
  ];

  const metrics: Metric[] = [
    {
      id: 'utilization',
      label: 'Knowledge Utilization Rate',
      value: '87.3%',
      change: 5.2,
      changeLabel: 'vs last month',
      icon: 'ChartBarIcon',
      color: 'bg-gradient-to-br from-accent-cyan to-accent-gold'
    },
    {
      id: 'accuracy',
      label: 'Citation Accuracy Score',
      value: '94.8%',
      change: 2.1,
      changeLabel: 'vs last month',
      icon: 'CheckBadgeIcon',
      color: 'bg-gradient-to-br from-accent-gold to-accent-cyan'
    },
    {
      id: 'freshness',
      label: 'Content Freshness',
      value: '76.5%',
      change: -3.4,
      changeLabel: 'needs attention',
      icon: 'ClockIcon',
      color: 'bg-gradient-to-br from-accent-cyan to-accent-gold'
    },
    {
      id: 'gaps',
      label: 'Identified Gaps',
      value: '23',
      change: -12.5,
      changeLabel: 'vs last month',
      icon: 'ExclamationTriangleIcon',
      color: 'bg-gradient-to-br from-accent-gold to-accent-cyan'
    }
  ];

  const knowledgeNodes: KnowledgeNode[] = [
    {
      id: '1',
      title: 'Getting Started with API Integration',
      source: 'Notion',
      citations: 342,
      satisfaction: 96,
      freshness: 'fresh',
      category: 'integration'
    },
    {
      id: '2',
      title: 'Troubleshooting Authentication Errors',
      source: 'Confluence',
      citations: 289,
      satisfaction: 91,
      freshness: 'fresh',
      category: 'troubleshooting'
    },
    {
      id: '3',
      title: 'Advanced Webhook Configuration',
      source: 'Google Drive',
      citations: 234,
      satisfaction: 88,
      freshness: 'aging',
      category: 'configuration'
    },
    {
      id: '4',
      title: 'Rate Limiting Best Practices',
      source: 'Notion',
      citations: 198,
      satisfaction: 93,
      freshness: 'fresh',
      category: 'best-practices'
    },
    {
      id: '5',
      title: 'Database Migration Guide',
      source: 'Confluence',
      citations: 167,
      satisfaction: 85,
      freshness: 'stale',
      category: 'migration'
    },
    {
      id: '6',
      title: 'Security Token Management',
      source: 'Jira',
      citations: 156,
      satisfaction: 94,
      freshness: 'fresh',
      category: 'security'
    }
  ];

  const recommendations: Recommendation[] = [
    {
      id: '1',
      type: 'update',
      title: 'Database Migration Guide requires content refresh',
      reason: 'Last updated 8 months ago, showing declining satisfaction scores',
      priority: 'high',
      impact: 'High impact on 167 citations',
      source: 'Confluence'
    },
    {
      id: '2',
      type: 'create',
      title: 'Create guide for GraphQL API implementation',
      reason: 'Identified gap with 45 related support tickets in last 30 days',
      priority: 'high',
      impact: 'Potential to reduce 15% of support volume'
    },
    {
      id: '3',
      type: 'replicate',
      title: 'Expand API Integration guide format to other topics',
      reason: 'Highest satisfaction score (96%) and citation rate',
      priority: 'medium',
      impact: 'Could improve overall knowledge base effectiveness',
      source: 'Notion'
    },
    {
      id: '4',
      type: 'update',
      title: 'Advanced Webhook Configuration needs technical review',
      reason: 'Content aging with satisfaction trending downward',
      priority: 'medium',
      impact: 'Affects 234 citations',
      source: 'Google Drive'
    }
  ];

  const contentArticles: ContentArticle[] = [
    {
      id: '1',
      title: 'Getting Started with API Integration',
      source: 'Notion',
      citations: 342,
      satisfaction: 96,
      lastUpdated: '2026-02-15',
      status: 'approved',
      category: 'integration',
      views: 12453
    },
    {
      id: '2',
      title: 'Troubleshooting Authentication Errors',
      source: 'Confluence',
      citations: 289,
      satisfaction: 91,
      lastUpdated: '2026-02-20',
      status: 'approved',
      category: 'troubleshooting',
      views: 9876
    },
    {
      id: '3',
      title: 'Advanced Webhook Configuration',
      source: 'Google Drive',
      citations: 234,
      satisfaction: 88,
      lastUpdated: '2025-11-10',
      status: 'pending',
      category: 'configuration',
      views: 8234
    },
    {
      id: '4',
      title: 'Rate Limiting Best Practices',
      source: 'Notion',
      citations: 198,
      satisfaction: 93,
      lastUpdated: '2026-02-18',
      status: 'approved',
      category: 'best-practices',
      views: 7654
    },
    {
      id: '5',
      title: 'Database Migration Guide',
      source: 'Confluence',
      citations: 167,
      satisfaction: 85,
      lastUpdated: '2025-06-22',
      status: 'draft',
      category: 'migration',
      views: 6543
    },
    {
      id: '6',
      title: 'Security Token Management',
      source: 'Jira',
      citations: 156,
      satisfaction: 94,
      lastUpdated: '2026-02-12',
      status: 'approved',
      category: 'security',
      views: 5987
    },
    {
      id: '7',
      title: 'REST API Error Handling',
      source: 'Slack',
      citations: 143,
      satisfaction: 89,
      lastUpdated: '2026-01-28',
      status: 'approved',
      category: 'troubleshooting',
      views: 5432
    },
    {
      id: '8',
      title: 'OAuth 2.0 Implementation Guide',
      source: 'Notion',
      citations: 128,
      satisfaction: 92,
      lastUpdated: '2026-02-05',
      status: 'approved',
      category: 'security',
      views: 4876
    }
  ];

  const handleSourceToggle = (sourceId: string) => {
    if (!isHydrated) return;
    setSelectedSources(prev =>
      prev.includes(sourceId)
        ? prev.filter(id => id !== sourceId)
        : [...prev, sourceId]
    );
  };

  const handleTypeToggle = (typeId: string) => {
    if (!isHydrated) return;
    setSelectedTypes(prev =>
      prev.includes(typeId)
        ? prev.filter(id => id !== typeId)
        : [...prev, typeId]
    );
  };

  const handleNodeClick = (nodeId: string) => {
    if (!isHydrated) return;
    console.log('Node clicked:', nodeId);
  };

  const handleArticleClick = (articleId: string) => {
    if (!isHydrated) return;
    console.log('Article clicked:', articleId);
  };

  const handleBulkAction = (action: string, articleIds: string[]) => {
    if (!isHydrated) return;
    console.log('Bulk action:', action, articleIds);
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:352:6" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="352" data-component-end-line="366" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
        <div data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:353:8" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="353" data-component-end-line="356" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-4%22%7D" className="flex items-center space-x-4">
          <div data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:354:10" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="354" data-component-end-line="354" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-14%20w-64%20bg-secondary%2F50%20rounded-xl%20animate-pulse%22%7D" className="h-14 w-64 bg-secondary/50 rounded-xl animate-pulse" />
          <div data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:355:10" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="355" data-component-end-line="355" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-14%20w-56%20bg-secondary%2F50%20rounded-xl%20animate-pulse%22%7D" className="h-14 w-56 bg-secondary/50 rounded-xl animate-pulse" />
        </div>
        <div data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:357:8" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="357" data-component-end-line="361" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20sm%3Agrid-cols-2%20lg%3Agrid-cols-4%20gap-4%22%7D" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:359:12" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="359" data-component-end-line="359" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-32%20bg-secondary%2F50%20rounded-xl%20animate-pulse%22%7D" key={i} className="h-32 bg-secondary/50 rounded-xl animate-pulse" />
          ))}
        </div>
        <div data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:362:8" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="362" data-component-end-line="365" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-12%20gap-6%22%7D" className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:363:10" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="363" data-component-end-line="363" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-9%20h-96%20bg-secondary%2F50%20rounded-xl%20animate-pulse%22%7D" className="lg:col-span-9 h-96 bg-secondary/50 rounded-xl animate-pulse" />
          <div data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:364:10" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="364" data-component-end-line="364" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-3%20h-96%20bg-secondary%2F50%20rounded-xl%20animate-pulse%22%7D" className="lg:col-span-3 h-96 bg-secondary/50 rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:371:4" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="371" data-component-end-line="404" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
      <div data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:372:6" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="372" data-component-end-line="383" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20sm%3Aflex-row%20items-start%20sm%3Aitems-center%20space-y-4%20sm%3Aspace-y-0%20sm%3Aspace-x-4%22%7D" className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
        <KnowledgeSourceSelector data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:373:8" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="373" data-component-end-line="377" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="KnowledgeSourceSelector" data-component-content="%7B%22elementName%22%3A%22KnowledgeSourceSelector%22%7D"
          sources={knowledgeSources}
          selectedSources={selectedSources}
          onSourceToggle={handleSourceToggle}
        />
        <ContentTypeFilter data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:378:8" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="378" data-component-end-line="382" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="ContentTypeFilter" data-component-content="%7B%22elementName%22%3A%22ContentTypeFilter%22%7D"
          contentTypes={contentTypes}
          selectedTypes={selectedTypes}
          onTypeToggle={handleTypeToggle}
        />
      </div>

      <MetricsStrip data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:385:6" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="385" data-component-end-line="385" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="MetricsStrip" data-component-content="%7B%22elementName%22%3A%22MetricsStrip%22%7D" metrics={metrics} />

      <div data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:387:6" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="387" data-component-end-line="397" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-12%20gap-6%22%7D" className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:388:8" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="388" data-component-end-line="393" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-9%22%7D" className="lg:col-span-9">
          <KnowledgeMapVisualization data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:389:10" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="389" data-component-end-line="392" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="KnowledgeMapVisualization" data-component-content="%7B%22elementName%22%3A%22KnowledgeMapVisualization%22%7D"
            nodes={knowledgeNodes}
            onNodeClick={handleNodeClick}
          />
        </div>
        <div data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:394:8" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="394" data-component-end-line="396" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-3%22%7D" className="lg:col-span-3">
          <ContentRecommendations data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:395:10" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="395" data-component-end-line="395" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="ContentRecommendations" data-component-content="%7B%22elementName%22%3A%22ContentRecommendations%22%7D" recommendations={recommendations} />
        </div>
      </div>

      <ContentPerformanceTable data-component-id="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx:399:6" data-component-path="src/app/knowledge-performance/components/KnowledgePerformanceInteractive.tsx" data-component-line="399" data-component-end-line="403" data-component-file="KnowledgePerformanceInteractive.tsx" data-component-name="ContentPerformanceTable" data-component-content="%7B%22elementName%22%3A%22ContentPerformanceTable%22%7D"
        articles={contentArticles}
        onArticleClick={handleArticleClick}
        onBulkAction={handleBulkAction}
      />
    </div>
  );
}